/**
 * NotificationPermissionCard — "notifications on this device", platform-aware
 * (2026-09-08). Mounted in Settings → notifications and in the mobile menu's
 * notifications section; renders ONE state from notificationCapability:
 *
 *   unsupported      → one honest line;
 *   needs-install    → iOS: the home-screen steps + the "get the app" pointer;
 *   default          → ONE primary "turn on notifications" — the permission
 *                      prompt runs INSIDE that click (requestPushPermission),
 *                      never on mount;
 *   denied           → per-platform recovery steps + "check again";
 *   granted-no-token → allowed, not registered yet: "register this device";
 *   granted          → "on for this device" + the account's device list, each
 *                      removable.
 *
 * NEVER a disabled control: a busy button keeps working and says so
 * (aria-busy + copy). House voice: lowercase, plain words; var(--brand-*)
 * tokens only. The presentational view is exported for tests; the default
 * export is the live container, which draws `useNotificationPermission`'s
 * answer (src/hooks/useNotificationPermission.ts: auth, capability, the
 * request, the re-check and the device list, settings part 2a, 2026-10-11) —
 * the hook the new design's settings page draws from too.
 */
import React from 'react';
import styled from 'styled-components';
import {
  describeNotificationState,
  recoverySteps,
  type NotificationPlatform,
  type NotificationState,
} from '../../services/notificationCapability';
import { useNotificationPermission } from '../../hooks/useNotificationPermission';
import { deviceLabel, lastSeenLabel, type NotificationDeviceRow } from '../../hooks/notificationPermissionModel';

// ── styles ───────────────────────────────────────────────────────────────────

const Card = styled.section<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$compact ? '0.5rem' : '0.75rem')};
  padding: ${(p) => (p.$compact ? '0.75rem' : '1rem 1.125rem')};
  background: linear-gradient(145deg, var(--brand-canvas), #ffffff);
  border: 1px solid var(--brand-line);
  border-radius: 12px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--brand-text);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`;

const Title = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: lowercase;
  color: var(--brand-black);
`;

const StatusDot = styled.span<{ $tone: 'on' | 'off' | 'blocked' }>`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.$tone === 'on' ? 'var(--brand-gold)' : p.$tone === 'blocked' ? 'var(--brand-danger)' : 'var(--brand-line-strong)')};
`;

const Line = styled.p`
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.45;
  color: var(--brand-text-secondary);
`;

const Steps = styled.ol`
  margin: 0;
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.82rem;
  line-height: 1.45;
  color: var(--brand-text-secondary);
`;

const PrimaryButton = styled.button`
  appearance: none;
  border: 1px solid var(--brand-gold);
  background: var(--brand-gold);
  color: var(--brand-black);
  border-radius: 999px;
  padding: 0.5rem 0.95rem;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: lowercase;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: var(--brand-gold-hover);
    border-color: var(--brand-gold-hover);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--brand-gold-hover);
    outline-offset: 2px;
  }
`;

const QuietButton = styled.button`
  appearance: none;
  border: 1px solid var(--brand-line-strong);
  background: transparent;
  color: var(--brand-text);
  border-radius: 999px;
  padding: 0.45rem 0.85rem;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: lowercase;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: var(--brand-surface);
  }

  &:focus-visible {
    outline: 2px solid var(--brand-gold-hover);
    outline-offset: 2px;
  }
`;

const DeviceList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const DeviceRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.45rem 0.6rem;
  border: 1px solid var(--brand-line);
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.8rem;
`;

const DeviceName = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
  color: var(--brand-text);

  small {
    color: var(--brand-text-secondary);
    font-size: 0.72rem;
  }
`;

const RemoveButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: var(--brand-text-secondary);
  font-family: inherit;
  font-size: 0.76rem;
  text-transform: lowercase;
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;

  &:hover {
    color: var(--brand-danger);
    background: var(--brand-surface);
  }

  &:focus-visible {
    outline: 2px solid var(--brand-gold-hover);
    outline-offset: 1px;
  }
`;

// ── view ─────────────────────────────────────────────────────────────────────

export type { NotificationDeviceRow };

export interface NotificationPermissionCardViewProps {
  state: NotificationState;
  platform: NotificationPlatform;
  /** A request/re-check is in flight — the control stays live and says so. */
  busy?: boolean;
  devices?: NotificationDeviceRow[];
  onRequest: () => void;
  onRecheck: () => void;
  onRemoveDevice?: (token: string) => void;
  compact?: boolean;
}

const IOS_INSTALL_STEPS = [
  'open prest0.ai in safari',
  'tap the share button, then "add to home screen"',
  'open prest0 from your home screen and come back to this card',
];

export const NotificationPermissionCardView: React.FC<NotificationPermissionCardViewProps> = ({
  state,
  platform,
  busy,
  devices,
  onRequest,
  onRecheck,
  onRemoveDevice,
  compact,
}) => {
  const tone: 'on' | 'off' | 'blocked' = state === 'granted' ? 'on' : state === 'denied' ? 'blocked' : 'off';
  return (
    <Card data-notification-permission-card="" data-notification-state={state} data-notification-platform={platform} $compact={compact}>
      <Row>
        <TitleWrap>
          <StatusDot $tone={tone} aria-hidden="true" />
          <Title>notifications on this device</Title>
        </TitleWrap>
        {state === 'default' && (
          <PrimaryButton
            type="button"
            data-notification-action="request"
            aria-busy={busy ? 'true' : undefined}
            onClick={onRequest}
          >
            {busy ? 'turning on…' : 'turn on notifications'}
          </PrimaryButton>
        )}
        {state === 'granted-no-token' && (
          <PrimaryButton
            type="button"
            data-notification-action="request"
            aria-busy={busy ? 'true' : undefined}
            onClick={onRequest}
          >
            {busy ? 'registering…' : 'register this device'}
          </PrimaryButton>
        )}
        {(state === 'denied' || state === 'needs-install') && (
          <QuietButton
            type="button"
            data-notification-action="recheck"
            aria-busy={busy ? 'true' : undefined}
            onClick={onRecheck}
          >
            {busy ? 'checking…' : 'check again'}
          </QuietButton>
        )}
      </Row>
      <Line data-notification-summary="">{describeNotificationState(state, platform)}</Line>
      {state === 'needs-install' && platform === 'ios-safari' && (
        <>
          <Steps data-notification-steps="install">
            {IOS_INSTALL_STEPS.map((step) => <li key={step}>{step}</li>)}
          </Steps>
          <Line data-notification-get-app="">get the app: it is the same prest0, one tap away on your home screen — no store, no download.</Line>
        </>
      )}
      {state === 'denied' && (
        <Steps data-notification-steps="recovery">
          {recoverySteps(platform).map((step) => <li key={step}>{step}</li>)}
        </Steps>
      )}
      {(state === 'granted' || state === 'granted-no-token') && devices && devices.length > 0 && (
        <DeviceList data-notification-devices="" aria-label="devices getting notifications">
          {devices.map((d) => (
            <DeviceRow key={d.token} data-notification-device={d.token} data-this-device={d.isThisDevice ? 'true' : undefined}>
              <DeviceName>
                <span>{deviceLabel(d)}{d.isThisDevice ? ' · this device' : ''}</span>
                {lastSeenLabel(d.lastSeen) && <small>{lastSeenLabel(d.lastSeen)}</small>}
              </DeviceName>
              {onRemoveDevice && (
                <RemoveButton
                  type="button"
                  data-notification-remove-device={d.token}
                  aria-label={`stop notifications on ${deviceLabel(d)}`}
                  onClick={() => onRemoveDevice(d.token)}
                >
                  remove
                </RemoveButton>
              )}
            </DeviceRow>
          ))}
        </DeviceList>
      )}
    </Card>
  );
};

// ── container ────────────────────────────────────────────────────────────────

export const NotificationPermissionCard: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <NotificationPermissionCardView {...useNotificationPermission()} compact={compact} />
);

export default NotificationPermissionCard;
